import { showNotification } from '@mantine/notifications';
import { Permission, Role, UserView } from 'api/user';
import { InfiniteListItem } from 'components/InfiniteList/InfiniteList';
import {
  startImpersonating,
  stopImpersonating,
  useImpersonation,
} from 'helpers/impersonation';
import { can } from 'helpers/userUtils';
import React from 'react';
import { useMe } from 'store/userStore';
import { UserSelect } from 'views/UserSearch/components/UserSelect';

export const Impersonator = () => {
  const me = useMe();
  const { isImpersonating, isPending } = useImpersonation();

  /**
   * While impersonating, `me` is the impersonated user — who is a coach and so
   * cannot impersonate — which is why the picker's visibility falls back to
   * `isImpersonating`. Without that it would vanish the moment it was used,
   * stranding the admin with no way back.
   */
  const canImpersonate =
    can(me, Permission.ADMINISTRATION_IMPERSONATION) || isImpersonating;

  const [busy, setBusy] = React.useState(false);

  const filters = React.useMemo(
    () => ({
      // Include the AI coach (Rose) so devs can impersonate it like any coach.
      query: {
        userRole: { role: { in: [Role.THERAPIST, Role.COACH, Role.AI_COACH] } },
      },
      include: ['userRole'],
    }),
    []
  );

  const handleSwitchUser = React.useCallback(
    async (option: InfiniteListItem<UserView> | null) => {
      const targetUserId = option?.value.userId ?? null;
      setBusy(true);
      try {
        // Switching straight from one user to another is supported — the
        // server authorizes against the admin behind the session, not the user
        // currently being worn — so there is no stop-then-start round trip.
        if (targetUserId) {
          await startImpersonating(targetUserId);
        } else {
          await stopImpersonating();
        }
        // Full reload rather than a query invalidation: the session cookie has
        // changed, so every cached response in the app now belongs to someone
        // else.
        window.location.replace('/');
      } catch (error) {
        setBusy(false);
        showNotification({
          title: 'Impersonation failed',
          message:
            error instanceof Error ? error.message : 'Could not switch user',
          color: 'red',
        });
      }
    },
    []
  );

  return canImpersonate ? (
    <UserSelect
      sx={{ width: '250px' }}
      placeholder="Pick a coach..."
      filters={filters}
      value={isImpersonating ? (me?.userId ?? null) : null}
      onChange={handleSwitchUser}
      disabled={busy || isPending}
      clearable
      showRole
    />
  ) : null;
};
